/**
 * Script view — a Notion-style document projection of the board's locked
 * "Original Script" column (F-28). Every block IS one row's cell: the block
 * list is `sortedRowIds` and a block's text is that row's source-column cell,
 * so the grid and the script are the same data — switching views needs no
 * conversion, and every edit here syncs live like any cell edit.
 *
 * Block UX (the Notion behaviors that matter for script writing):
 *  - always-editable blocks; commits are debounced + on blur (LWW like cells)
 *  - Enter splits the block at the caret → a new row below
 *  - Backspace at block start merges into the previous block (deletes the row)
 *  - Delete at block end merges the next block in
 *  - multi-line paste becomes one block per line (rows via bulk insert)
 *  - Cmd/Ctrl+C over a multi-block highlight copies every line (and Cmd+X cuts
 *    them); pasting anywhere in the script rebuilds them as separate blocks
 *  - typing through an IME (Traditional Chinese, Japanese, Korean …) leaves the
 *    keys alone until the candidate is chosen — see core/keyboard.ts
 *  - drag the ⠿ handle to reorder rows
 *  - highlight text → a small menu → "Comment" (a thread anchored to this cell
 *    with the text range, `Anchor.sel`) or "Ask AI"; commented ranges render as
 *    persistent highlights (skipped when resolved), and the pin shows on the
 *    cell in grid view too.
 *  - the same menu FORMATS the highlighted characters (F-17-marks): bold,
 *    strike-through, ink color and highlight color, stored per range on the
 *    cell (@kola/shared text-marks.ts) and rendered in grid view too.
 *  - drag ACROSS blocks → the highlight becomes a range of whole lines, and the
 *    menu offers "Ask AI" on the passage (see ./selection.ts for why crossing a
 *    block boundary changes what a highlight means).
 *
 * Every mutation goes through BoardSession (LOCAL_ORIGIN → per-user undo,
 * frozen-state guards). Structural ops (split/merge/paste) are one
 * `session.batch` = one undo step each. The AI panel is the one thing here that
 * mutates NOTHING — it reads the script and answers a question about it.
 */
import {
  bgmColumns,
  parseCellKey,
  segmentMarkedText,
  shortRuns,
  type Anchor,
  type BoardSummary,
  type CellTextMarks,
  type CellTextType,
  type CommentThreadInfo,
  type ShortState,
  type TextSegment,
  type TextTypeSwitch,
} from "@kola/shared";
import {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ClipboardEvent as ReactClipboardEvent,
  type DragEvent,
  type KeyboardEvent as ReactKeyboardEvent,
  type ReactNode,
} from "react";
import { useStore } from "zustand";

import { isImeComposing, isImeEnterEcho, isTypingTarget } from "../../core/keyboard.js";
import type { BoardSession } from "../../core/session.js";
import { useThemeColors, type ThemeColors } from "../../core/useThemeColors.js";
import { formatDateKeyWithWeekday } from "../../ui/date-key.js";
import { markClassName, markStyle } from "../../ui/MarkedText.js";
import {
  applyTextTypePick,
  matchTextTypes,
  textTypeBrackets,
  textTypeOptions,
  slashQueryAt,
  textTypeSwatch,
  typeRunEdges,
  type TextTypeOption,
} from "../../ui/text-type.js";
import { useTextKinds } from "../text-kinds/store.js";
import { TextFormatControls, type TextFormatActions } from "../../ui/TextFormatControls.js";
import { formatShortcut, orderedRange, readFormatState } from "../../ui/text-format.js";
import { showToast } from "../../ui/toast.js";
import { CommentInput, ThreadView } from "../comments/ThreadPopover.js";
import { BrainstormPopover, SparkleIcon } from "./BrainstormPopover.js";
import { buildBrainstormContext, capSelection } from "./brainstorm.js";
import {
  SCRIPT_BLOCKS_MIME,
  blocksToText,
  decodeBlocks,
  encodeBlocks,
  textToBlocks,
} from "./clipboard.js";
import {
  selectedRowIds,
  selectionLines,
  toggleBlockInSelection,
  type ScriptSelection,
} from "./selection.js";

const ROW_DRAG_MIME = "application/x-kola-script-row";

/** A comment thread pinned to a text range of one block (resolved offsets). */
export interface RangeHighlight {
  threadId: string;
  start: number;
  end: number;
}

interface CellSel {
  start: number;
  end: number;
  quote: string;
}

/**
 * Re-verify a stored text range against the CURRENT cell text: exact offsets
 * first, then a quote search, else null (the thread stays reachable from the
 * block's comment badge — it just has no highlight anymore).
 */
export function resolveSelRange(
  text: string,
  sel: CellSel,
): { start: number; end: number } | null {
  if (sel.quote.length === 0) return null;
  if (
    sel.end <= text.length &&
    sel.start < sel.end &&
    text.slice(sel.start, sel.end) === sel.quote
  ) {
    return { start: sel.start, end: sel.end };
  }
  const found = text.indexOf(sel.quote);
  if (found >= 0) return { start: found, end: found + sel.quote.length };
  return null;
}

/** Non-overlapping, sorted highlight list for one block's text. */
export function highlightsFor(
  text: string,
  threads: readonly CommentThreadInfo[],
): RangeHighlight[] {
  const ranges: RangeHighlight[] = [];
  for (const thread of threads) {
    if (thread.resolved) continue;
    const anchor = thread.anchor;
    if (anchor.kind !== "cell" || anchor.sel === undefined) continue;
    const resolved = resolveSelRange(text, anchor.sel);
    if (resolved !== null) ranges.push({ threadId: thread.id, ...resolved });
  }
  ranges.sort((a, b) => a.start - b.start || a.end - b.end);
  // Drop overlaps (first wins) — nested marks inside a mirrored textarea
  // would break the character-for-character metric match.
  const out: RangeHighlight[] = [];
  let cursor = 0;
  for (const range of ranges) {
    if (range.start < cursor) continue;
    out.push(range);
    cursor = range.end;
  }
  return out;
}

/** One run of the mirror layer: formatting (F-17-marks) plus comment tint. */
interface BlockSegment extends TextSegment {
  threadId?: string;
}

/**
 * Split a block's text into the runs its mirror layer paints — format marks,
 * F-17-type line kinds and comment highlights cut the SAME string, in one
 * flat list.
 *
 * Flat, never nested, for the reason `highlightsFor` already drops overlapping
 * comment ranges: the mirror has to stay a character-for-character metric
 * match of the textarea over it, and a `<mark>` inside a `<span>` inside a
 * `<mark>` is one more chance for a browser to disagree about a line break.
 */
export function blockSegments(
  text: string,
  marks: CellTextMarks | undefined,
  types: readonly TextTypeSwitch[] | undefined,
  highlights: readonly RangeHighlight[],
): BlockSegment[] {
  const formatted = segmentMarkedText(text, marks, types);
  if (highlights.length === 0) return formatted;
  const out: BlockSegment[] = [];
  let offset = 0;
  let next = 0; // highlights are sorted and non-overlapping (highlightsFor)
  for (const segment of formatted) {
    const end = offset + segment.text.length;
    let cursor = offset;
    while (cursor < end) {
      let hit = highlights[next];
      while (hit !== undefined && hit.end <= cursor) hit = highlights[++next];
      const inside = hit !== undefined && hit.start <= cursor;
      const stop = hit === undefined ? end : Math.min(end, inside ? hit.end : hit.start);
      const part: BlockSegment = { ...segment, text: text.slice(cursor, stop) };
      if (inside && hit !== undefined) part.threadId = hit.threadId;
      out.push(part);
      cursor = stop;
    }
    offset = end;
  }
  return out;
}

/** The block-range half of a ScriptSelection, as ScriptView stores it. */
type BlockSel = {
  anchorRowId: string;
  focusRowId: string;
  /** Rows added with Cmd/Ctrl+click — see toggleBlockInSelection. */
  addedRowIds?: readonly string[];
};

export function ScriptView({
  session,
  pageWidth,
  summary,
}: {
  session: BoardSession;
  /** Script page max-width (px) — the "⋯" menu's slider (BoardScreen). */
  pageWidth?: number;
  /** F-142: the board's summary — a Shorts week gets a heading over each short. */
  summary?: BoardSummary | null;
}) {
  const sheetId = useStore(session.store, (s) => s.sheetId);
  const structureVersion = useStore(session.store, (s) => s.structureVersion);
  const commentsVersion = useStore(session.store, (s) => s.commentsVersion);
  const frozen = useStore(session.store, (s) => s.frozen);

  const colId = useMemo(
    () => (sheetId === null ? undefined : session.board.sourceColumnId(sheetId)),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- column set changes bump structureVersion
    [session, sheetId, structureVersion],
  );

  // F-142: the `Short` column of a Shorts week. Its cells say which short a
  // row belongs to, so an edit there re-renders the headings the way a
  // script edit re-renders the blocks.
  const shortColId = useMemo(
    () =>
      sheetId === null || summary?.kind !== "shorts"
        ? undefined
        : bgmColumns(session.board, sheetId).shortColId,
    // eslint-disable-next-line react-hooks/exhaustive-deps -- column set changes bump structureVersion
    [session, sheetId, structureVersion, summary?.kind],
  );

  // Re-render on source-column (and Short-column) cell edits, local or remote.
  const [cellsTick, setCellsTick] = useState(0);
  useEffect(() => {
    if (colId === undefined && shortColId === undefined) return;
    return session.onCellsChanged((keys) => {
      for (const key of keys) {
        const parsed = parseCellKey(key);
        if (parsed !== null && (parsed.colId === colId || parsed.colId === shortColId)) {
          setCellsTick((t) => t + 1);
          return;
        }
      }
    });
  }, [session, colId, shortColId]);

  // F-17-marks: the two tokens a highlighted run's readable-ink derivation
  // computes from. One read per theme flip, shared by every block.
  const theme = useThemeColors();

  const rowIds = useMemo(
    () => (sheetId === null ? [] : session.board.sortedRowIds(sheetId)),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- row set/order changes bump structureVersion
    [session, sheetId, structureVersion],
  );

  // F-142: on a Shorts week, a heading over the first row of each short —
  // "Short 3 · Wed Sep 30 · working title" — so the page reads as five
  // scripts rather than one. Keyed by the short's top row; empty elsewhere.
  const shortHeadings = useMemo(() => {
    const headings = new Map<string, ShortHeadingInfo>();
    if (sheetId === null || summary === null || summary === undefined || summary.kind !== "shorts") {
      return headings;
    }
    const slots = new Map(summary.shorts.map((slot) => [slot.slot, slot]));
    for (const run of shortRuns(session.board, sheetId)) {
      const slot = slots.get(run.slot);
      headings.set(run.topRowId, {
        slot: run.slot,
        title: slot?.title ?? "",
        releaseDate: slot?.releaseDate,
        state: slot?.state ?? "planned",
      });
    }
    return headings;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- row/Short-cell changes bump structureVersion/cellsTick
  }, [session, sheetId, structureVersion, cellsTick, summary]);

  /** Cell-anchored threads of the source column, grouped by row. */
  const threadsByRow = useMemo(() => {
    const map = new Map<string, CommentThreadInfo[]>();
    if (colId === undefined) return map;
    for (const thread of session.board.listThreads()) {
      if (thread.anchor.kind !== "cell" || thread.anchor.colId !== colId) continue;
      const list = map.get(thread.anchor.rowId);
      if (list) list.push(thread);
      else map.set(thread.anchor.rowId, [thread]);
    }
    return map;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- thread changes bump commentsVersion
  }, [session, colId, commentsVersion]);

  /* ------------------------- focus management ------------------------- */

  const blockRefs = useRef(new Map<string, HTMLTextAreaElement>());
  const pendingFocus = useRef<{ rowId: string; caret: number } | null>(null);

  useEffect(() => {
    const request = pendingFocus.current;
    if (request === null) return;
    const el = blockRefs.current.get(request.rowId);
    if (el) {
      pendingFocus.current = null;
      el.focus();
      const caret = Math.min(request.caret, el.value.length);
      el.setSelectionRange(caret, caret);
    }
  });

  const focusBlock = useCallback((rowId: string, caret: number) => {
    pendingFocus.current = { rowId, caret };
    const el = blockRefs.current.get(rowId);
    if (el) {
      pendingFocus.current = null;
      el.focus();
      const clamped = Math.min(caret, el.value.length);
      el.setSelectionRange(clamped, clamped);
    }
  }, []);

  /* ------------------------- structural edits ------------------------- */

  const textOf = useCallback(
    (rowId: string): string =>
      sheetId === null || colId === undefined
        ? ""
        : session.board.getCellText(sheetId, rowId, colId),
    [session, sheetId, colId],
  );

  /**
   * F-17-marks of one block's cell. Re-read per render (the block list
   * re-renders on `cellsTick`, which every cells-map change bumps) rather than
   * memoized per row: a mark write and a text write land in the same
   * transaction, so one source of truth and no cache to invalidate.
   */
  const marksOf = useCallback(
    (rowId: string): CellTextMarks | undefined =>
      sheetId === null || colId === undefined
        ? undefined
        : session.board.getCell(sheetId, rowId, colId)?.marks,
    [session, sheetId, colId],
  );

  /**
   * F-17-type switches of one block's cell (undefined when it has none).
   * Re-read per render for the same reason `marksOf` is, and through the
   * narrow accessor rather than a whole snapshot — this is asked once per
   * block on every `cellsTick`.
   */
  const typeOf = useCallback(
    (rowId: string): readonly TextTypeSwitch[] | undefined => {
      if (sheetId === null || colId === undefined) return undefined;
      const switches = session.board.getCellTextTypes(sheetId, rowId, colId);
      return switches.length === 0 ? undefined : switches;
    },
    [session, sheetId, colId],
  );

  /**
   * Push a block's uncommitted draft into the doc BEFORE a mark is written
   * against it. Marks are character offsets into the cell text, and the block
   * commits on a 400 ms debounce — so "type a word, highlight it, hit B" would
   * otherwise apply offsets from the draft to the older text in the doc.
   *
   * Reads the live textarea rather than any React state: the draft lives in
   * the block, and the element is the one thing the parent can see it through.
   */
  const flushBlock = useCallback(
    (rowId: string) => {
      if (sheetId === null || colId === undefined) return;
      const el = blockRefs.current.get(rowId);
      if (el === undefined || el.value === textOf(rowId)) return;
      session.setCellValue(sheetId, rowId, colId, el.value);
    },
    [session, sheetId, colId, textOf],
  );

  const splitBlock = useCallback(
    (rowId: string, before: string, after: string) => {
      if (sheetId === null || colId === undefined) return;
      const index = rowIds.indexOf(rowId);
      if (index < 0) return;
      session.batch(() => {
        session.setCellValue(sheetId, rowId, colId, before);
        const newRowId = session.insertRow(sheetId, index + 1);
        if (newRowId !== null && after !== "") {
          session.setCellValue(sheetId, newRowId, colId, after);
        }
        if (newRowId !== null) pendingFocus.current = { rowId: newRowId, caret: 0 };
      });
    },
    [session, sheetId, colId, rowIds],
  );

  const mergeIntoPrevious = useCallback(
    (rowId: string, text: string) => {
      if (sheetId === null || colId === undefined) return;
      const index = rowIds.indexOf(rowId);
      if (index <= 0) return;
      const prevRowId = rowIds[index - 1]!;
      const prevText = textOf(prevRowId);
      session.batch(() => {
        session.setCellValue(sheetId, prevRowId, colId, prevText + text);
        session.deleteRow(sheetId, rowId);
      });
      // DEFERRED focus (not focusBlock): the target textarea still renders
      // the pre-merge value; focusing now would let React's value swap reset
      // the caret to the end. The structural change re-renders immediately,
      // and the post-render effect then places the caret at the junction.
      pendingFocus.current = { rowId: prevRowId, caret: prevText.length };
    },
    [session, sheetId, colId, rowIds, textOf],
  );

  const mergeNextIn = useCallback(
    (rowId: string, text: string) => {
      if (sheetId === null || colId === undefined) return;
      const index = rowIds.indexOf(rowId);
      if (index < 0 || index >= rowIds.length - 1) return;
      const nextRowId = rowIds[index + 1]!;
      const nextText = textOf(nextRowId);
      session.batch(() => {
        session.setCellValue(sheetId, rowId, colId, text + nextText);
        session.deleteRow(sheetId, nextRowId);
      });
      // Deferred for the same reason as mergeIntoPrevious.
      pendingFocus.current = { rowId, caret: text.length };
    },
    [session, sheetId, colId, rowIds, textOf],
  );

  const pasteLines = useCallback(
    (rowId: string, before: string, lines: string[], after: string) => {
      if (sheetId === null || colId === undefined || lines.length === 0) return;
      const index = rowIds.indexOf(rowId);
      if (index < 0) return;
      if (lines.length === 1) {
        session.setCellValue(sheetId, rowId, colId, before + lines[0]! + after);
        // Deferred like the merges below: the textarea still renders the
        // pre-paste value, so the caret is placed by the post-render effect.
        pendingFocus.current = { rowId, caret: before.length + lines[0]!.length };
        return;
      }
      // Line 1 stays in this block; every further line is a new row below,
      // with the caret-tail (`after`) appended to the LAST of them. One batch
      // = one transaction = one undo step (the nested insertRows joins it).
      session.batch(() => {
        session.setCellValue(sheetId, rowId, colId, before + lines[0]!);
        const rest = lines.slice(1);
        const newRows = session.insertRows(sheetId, rest.length, index + 1);
        newRows.forEach((newRowId, i) => {
          const isLast = i === newRows.length - 1;
          const line = rest[i] ?? "";
          const cellValue = isLast ? line + after : line;
          if (cellValue !== "") session.setCellValue(sheetId, newRowId, colId, cellValue);
          if (isLast) pendingFocus.current = { rowId: newRowId, caret: line.length };
        });
      });
    },
    [session, sheetId, colId, rowIds],
  );

  const addBlockAtEnd = useCallback(() => {
    if (sheetId === null || frozen) return;
    const newRowId = session.insertRow(sheetId, rowIds.length);
    if (newRowId !== null) focusBlock(newRowId, 0);
  }, [session, sheetId, frozen, rowIds.length, focusBlock]);

  /* --------------------------- row reorder ---------------------------- */

  const [dragRowId, setDragRowId] = useState<string | null>(null);
  const [dropIndex, setDropIndex] = useState<number | null>(null);
  const listRef = useRef<HTMLDivElement | null>(null);

  const indexFromPointer = useCallback(
    (clientY: number): number => {
      const list = listRef.current;
      if (!list) return 0;
      const blocks = [...list.querySelectorAll<HTMLElement>("[data-script-row]")].filter(
        (el) => el.dataset.scriptRow !== dragRowId,
      );
      let index = 0;
      for (const block of blocks) {
        const rect = block.getBoundingClientRect();
        if (clientY > rect.top + rect.height / 2) index++;
      }
      return index;
    },
    [dragRowId],
  );

  const onListDragOver = useCallback(
    (event: DragEvent) => {
      if (dragRowId === null || !event.dataTransfer.types.includes(ROW_DRAG_MIME)) return;
      event.preventDefault();
      event.dataTransfer.dropEffect = "move";
      setDropIndex(indexFromPointer(event.clientY));
    },
    [dragRowId, indexFromPointer],
  );

  const onListDrop = useCallback(
    (event: DragEvent) => {
      if (dragRowId === null || sheetId === null) return;
      event.preventDefault();
      const target = indexFromPointer(event.clientY);
      session.moveRow(sheetId, dragRowId, target);
      setDragRowId(null);
      setDropIndex(null);
    },
    [session, sheetId, dragRowId, indexFromPointer],
  );

  /* ------------------- highlighting: the selection menu ---------------- */

  /** Character range inside ONE block — the native textarea selection. */
  const [textSel, setTextSel] = useState<{ rowId: string; start: number; end: number } | null>(
    null,
  );
  /**
   * Whole-line range across blocks, set once a drag leaves the block it began
   * in. Mirrored into a ref because the drag's window listeners are installed
   * once and would otherwise close over a stale value.
   */
  const [blockSel, setBlockSel] = useState<BlockSel | null>(null);
  const blockSelRef = useRef<BlockSel | null>(null);

  const applyBlockSel = useCallback((next: BlockSel | null) => {
    blockSelRef.current = next;
    setBlockSel(next);
  }, []);

  /**
   * A pointer drag that started inside a block. While it is held, the row under
   * the pointer is read from a HIT TEST, not from `event.target`: a native text
   * drag keeps delivering pointermove to the textarea it began in no matter
   * where the cursor actually is, so the target would always name the anchor.
   */
  const beginSelectionDrag = useCallback(
    (rowId: string, el: HTMLTextAreaElement) => {
      applyBlockSel(null);
      const onMove = (event: PointerEvent) => {
        const hit = document.elementFromPoint(event.clientX, event.clientY);
        const overRow = hit?.closest<HTMLElement>("[data-script-row]")?.dataset.scriptRow;
        // Gutter, gap between blocks, or outside the window: keep whatever we
        // had rather than flickering the highlight off mid-drag.
        if (overRow === undefined) return;
        applyBlockSel(overRow === rowId ? null : { anchorRowId: rowId, focusRowId: overRow });
      };
      const onUp = () => {
        window.removeEventListener("pointermove", onMove);
        if (blockSelRef.current === null) return;
        // The starting textarea is still painting its own partial native
        // selection; collapse and blur it so the whole-line tint is the ONLY
        // highlight on screen. Two highlights disagreeing about what is
        // selected is worse than either one alone.
        el.setSelectionRange(0, 0);
        el.blur();
      };
      window.addEventListener("pointermove", onMove);
      window.addEventListener("pointerup", onUp, { once: true });
    },
    [applyBlockSel],
  );

  /**
   * Click-based block selection, the two conventions everyone already knows:
   * Shift+click extends a whole-line range from the current anchor, Cmd/Ctrl+
   * click toggles one block in or out. Both blur the textarea for the same
   * reason the drag does — a live caret plus a whole-line tint would be two
   * highlights disagreeing about what is selected.
   */
  const selectBlockByClick = useCallback(
    (rowId: string, mode: "extend" | "toggle", el: HTMLTextAreaElement) => {
      setTextSel(null);
      el.setSelectionRange(0, 0);
      el.blur();
      if (mode === "toggle") {
        const next = toggleBlockInSelection(
          blockSelRef.current === null ? null : { kind: "blocks", ...blockSelRef.current },
          rowId,
          rowIds,
        );
        applyBlockSel(next === null || next.kind === "text" ? null : next);
        return;
      }
      // Extend: keep the existing anchor so repeated Shift+clicks sweep from
      // one fixed end, and drop any Cmd+clicked extras — a range is a range.
      const anchor = blockSelRef.current?.anchorRowId ?? rowId;
      applyBlockSel({ anchorRowId: anchor, focusRowId: rowId });
    },
    [applyBlockSel, rowIds],
  );

  /**
   * Delete every selected block in ONE transaction, so the whole multi-delete
   * is a single sync unit and a single undo step (C6/C8 still police it row by
   * row). Deleting a row is just dropping its rowMeta entry — orphaned cells
   * are swept by snapshot compaction (board.ts deleteRow).
   */
  const deleteBlocks = useCallback(
    (targets: readonly string[]) => {
      if (sheetId === null || targets.length === 0 || frozen) return;
      // Where the caret lands afterwards: the block just below the last one
      // removed, else the one just above — never nothing, when rows remain.
      const lastIndex = rowIds.indexOf(targets[targets.length - 1]!);
      const remaining = rowIds.filter((id) => !targets.includes(id));
      const after =
        rowIds.slice(lastIndex + 1).find((id) => remaining.includes(id)) ??
        [...rowIds.slice(0, lastIndex)].reverse().find((id) => remaining.includes(id));

      session.batch(() => {
        for (const rowId of targets) session.deleteRow(sheetId, rowId);
      });
      applyBlockSel(null);
      setTextSel(null);
      if (after !== undefined) pendingFocus.current = { rowId: after, caret: 0 };
    },
    [session, sheetId, rowIds, frozen, applyBlockSel],
  );

  /**
   * A cross-block highlight has no caret, so it needs window-level keys: Esc
   * dismisses it and Delete/Backspace removes the blocks. The textarea guard
   * matters — with a caret inside a block, Backspace still means "merge into
   * the previous block" (ScriptBlock.onKeyDown), not "delete these rows".
   */
  useEffect(() => {
    if (blockSel === null) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        applyBlockSel(null);
        return;
      }
      if (event.key !== "Delete" && event.key !== "Backspace") return;
      const target = event.target as HTMLElement | null;
      const tag = target?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || target?.isContentEditable) return;
      event.preventDefault();
      deleteBlocks(selectedRowIdsRef.current);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [blockSel, applyBlockSel, deleteBlocks]);

  const selection = useMemo<ScriptSelection | null>(() => {
    // A cross-block range wins: while one exists the anchor block's own native
    // selection is a leftover of the drag, not what the user means.
    if (blockSel !== null) return { kind: "blocks", ...blockSel };
    if (textSel !== null) return { kind: "text", ...textSel };
    return null;
  }, [blockSel, textSel]);

  const selectedRows = useMemo(
    () => (selection === null ? [] : selectedRowIds(selection, rowIds)),
    [selection, rowIds],
  );
  const selectedRowSet = useMemo(() => new Set(selectedRows), [selectedRows]);
  // The keydown listener is installed once per selection; without this the
  // handler would close over the rows as they were when it was installed.
  const selectedRowIdsRef = useRef<readonly string[]>(selectedRows);
  selectedRowIdsRef.current = selectedRows;
  /**
   * The menu hangs off the FIRST highlighted row, not the row the drag ended
   * on. It renders ABOVE its row, so anchoring it to the last row would float
   * it over the second-to-last line — on top of the text the writer just
   * highlighted and is still reading. Above the first line it covers nothing,
   * and it lands in the same place whether the drag went up or down.
   */
  const menuRow = selectedRows[0];

  /**
   * Run a formatting write over the current CHARACTER selection: flush + mark
   * in ONE transaction, so it is one sync unit and one undo step, and the
   * text a mark refers to can never be mid-flight when the mark lands.
   */
  const markSelection = useCallback(
    (apply: (rowId: string, start: number, end: number) => void) => {
      if (sheetId === null || colId === undefined || frozen) return;
      if (textSel === null) return;
      const range = orderedRange(textSel.start, textSel.end);
      if (range === null) return;
      session.batch(() => {
        flushBlock(textSel.rowId);
        apply(textSel.rowId, range.start, range.end);
      });
    },
    [session, sheetId, colId, frozen, textSel, flushBlock],
  );

  /**
   * What the menu's format buttons show as ACTIVE. Resolved against the live
   * textarea value (the draft, which is what the writer is looking at), not
   * the committed text — the marks re-anchor by quote, so a not-yet-committed
   * keystroke shifts nothing here.
   */
  const formatState = useMemo(() => {
    if (textSel === null || sheetId === null || colId === undefined) return null;
    const range = orderedRange(textSel.start, textSel.end);
    if (range === null) return null;
    const text = blockRefs.current.get(textSel.rowId)?.value ?? textOf(textSel.rowId);
    return readFormatState(
      text,
      (kind) => session.board.getCellMarks(sheetId, textSel.rowId, colId, kind),
      range.start,
      range.end,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mark writes bump cellsTick
  }, [session, sheetId, colId, textSel, textOf, cellsTick]);

  /* ------------------------- clipboard (blocks) ----------------------- */

  /**
   * Replace a run of blocks with pasted ones, in ONE transaction: the first
   * target takes the first pasted block, the rest are inserted below it (before
   * the targets that are about to go, so the run keeps its place in the
   * document), and the remaining targets are deleted.
   */
  const replaceBlocks = useCallback(
    (targets: readonly string[], blocks: readonly string[]) => {
      if (sheetId === null || colId === undefined || frozen) return;
      const firstRowId = targets[0];
      if (firstRowId === undefined || blocks.length === 0) return;
      const index = rowIds.indexOf(firstRowId);
      if (index < 0) return;
      session.batch(() => {
        session.setCellValue(sheetId, firstRowId, colId, blocks[0]!);
        const rest = blocks.slice(1);
        if (rest.length > 0) {
          const newRows = session.insertRows(sheetId, rest.length, index + 1);
          newRows.forEach((newRowId, i) => {
            const value = rest[i] ?? "";
            if (value !== "") session.setCellValue(sheetId, newRowId, colId, value);
          });
          const lastRowId = newRows[newRows.length - 1];
          if (lastRowId !== undefined) {
            pendingFocus.current = { rowId: lastRowId, caret: (rest[rest.length - 1] ?? "").length };
          }
        } else {
          pendingFocus.current = { rowId: firstRowId, caret: blocks[0]!.length };
        }
        for (const rowId of targets.slice(1)) session.deleteRow(sheetId, rowId);
      });
      applyBlockSel(null);
      setTextSel(null);
    },
    [session, sheetId, colId, frozen, rowIds, applyBlockSel],
  );

  /**
   * Copy/cut/paste for a CROSS-BLOCK highlight. A character range inside one
   * block needs none of this — it is the textarea's own selection and the
   * browser copies it correctly. A block range is ours alone (the drag
   * collapsed and blurred the anchor textarea so two highlights would not
   * disagree, ./selection.ts), which also means the browser has nothing left to
   * copy: without these handlers Cmd+C over five highlighted lines quietly
   * copies nothing at all.
   *
   * Same shape as the grid's (GridTable): listeners installed once, reading the
   * latest closures through refs.
   */
  const copyRef = useRef<(event: ClipboardEvent) => void>(() => {});
  const cutRef = useRef<(event: ClipboardEvent) => void>(() => {});
  const pasteRef = useRef<(event: ClipboardEvent) => void>(() => {});

  /** The highlighted blocks' text, or null when this copy is not ours. */
  const blocksToCopy = (): string[] | null => {
    // A focused input (a comment composer, the AI question box) or a live DOM
    // selection means the user is copying THAT — native copy must win. The
    // question is who has FOCUS, not what the event names as its target:
    // Blink still targets the last-focused textarea on some copy paths, long
    // after a block selection blurred it.
    if (isTypingTarget(document.activeElement)) return null;
    if ((window.getSelection()?.toString() ?? "") !== "") return null;
    if (selection === null || selection.kind !== "blocks") return null;
    if (selectedRows.length === 0) return null;
    return selectedRows.map(textOf);
  };

  const writeBlocks = (event: ClipboardEvent, blocks: readonly string[]): void => {
    if (event.clipboardData === null) return;
    event.clipboardData.setData("text/plain", blocksToText(blocks));
    // The private flavour carries the boundaries a paste back into the script
    // must reproduce (./clipboard.ts).
    event.clipboardData.setData(SCRIPT_BLOCKS_MIME, encodeBlocks(blocks));
    event.preventDefault();
  };

  copyRef.current = (event: ClipboardEvent) => {
    const blocks = blocksToCopy();
    if (blocks !== null) writeBlocks(event, blocks);
  };

  cutRef.current = (event: ClipboardEvent) => {
    const blocks = blocksToCopy();
    if (blocks === null) return;
    writeBlocks(event, blocks);
    // On a frozen board the lines still reach the clipboard; only the removal
    // is refused (deleteBlocks itself checks) — half of a cut beats none of it.
    deleteBlocks(selectedRows);
  };

  pasteRef.current = (event: ClipboardEvent) => {
    // Inside a block the textarea's own onPaste handles it, with the caret.
    if (isTypingTarget(document.activeElement)) return;
    if (selection === null || selection.kind !== "blocks") return;
    if (selectedRows.length === 0 || event.clipboardData === null) return;
    const own = decodeBlocks(event.clipboardData.getData(SCRIPT_BLOCKS_MIME));
    const blocks = own ?? textToBlocks(event.clipboardData.getData("text/plain"));
    // An empty clipboard replaces nothing — an image paste is not ours either.
    if (blocks.length === 1 && blocks[0] === "") return;
    event.preventDefault();
    replaceBlocks(selectedRows, blocks);
  };

  useEffect(() => {
    const onCopy = (event: ClipboardEvent) => copyRef.current(event);
    const onCut = (event: ClipboardEvent) => cutRef.current(event);
    const onPaste = (event: ClipboardEvent) => pasteRef.current(event);
    document.addEventListener("copy", onCopy);
    document.addEventListener("cut", onCut);
    window.addEventListener("paste", onPaste);
    return () => {
      document.removeEventListener("copy", onCopy);
      document.removeEventListener("cut", onCut);
      window.removeEventListener("paste", onPaste);
    };
  }, []);

  /* -------------------- comments: compose + threads ------------------- */

  /** Open composer (frozen copy of the selection at click time). */
  const [composer, setComposer] = useState<{
    rowId: string;
    sel: CellSel;
  } | null>(null);
  /** Open thread popover, anchored under a block. */
  const [openThread, setOpenThread] = useState<{ rowId: string; threadId: string } | null>(
    null,
  );

  const submitComment = useCallback(
    (body: string) => {
      if (composer === null || colId === undefined) return;
      const anchor: Anchor = {
        kind: "cell",
        rowId: composer.rowId,
        colId,
        dx: 8,
        dy: 8,
        sel: composer.sel,
      };
      const threadId = session.createCommentThread(anchor, body);
      setComposer(null);
      if (threadId === null) {
        showToast("Could not add the comment (board is read-only right now)");
      } else {
        setOpenThread({ rowId: composer.rowId, threadId });
      }
    },
    [session, composer, colId],
  );

  /* --------------------------- ask the AI ----------------------------- */

  /**
   * The open brainstorm panel. Holds the SELECTION it was opened on, not the
   * text — the board is live, so the passage is re-read at ask time and a
   * follow-up asked five minutes later is about what the script says now.
   * `rowId` is only where the panel renders.
   */
  const [brainstorm, setBrainstorm] = useState<{
    rowId: string;
    selection: ScriptSelection;
  } | null>(null);

  const openBrainstorm = useCallback(() => {
    if (selection === null || selectedRows.length === 0) return;
    setBrainstorm({ rowId: selectedRows[selectedRows.length - 1]!, selection });
    // Drop the live highlight: the panel paints its own (the rows it is asking
    // about), and two highlights on screen would not say which one is the
    // question.
    applyBlockSel(null);
    setTextSel(null);
  }, [selection, selectedRows, applyBlockSel]);

  /** The highlighted lines the open panel is asking about, re-read each render. */
  const asking = useMemo(() => {
    if (brainstorm === null) return null;
    const lines = selectionLines(brainstorm.selection, rowIds, textOf);
    return { rows: new Set(selectedRowIds(brainstorm.selection, rowIds)), ...capSelection(lines) };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- cell edits bump cellsTick
  }, [brainstorm, rowIds, textOf, cellsTick]);

  /**
   * Snapshot the whole script plus the highlight, AT ASK TIME. Not memoized: it
   * walks every row on the board, and recomputing that on each keystroke in the
   * question box would be a real cost for a value nothing renders (same
   * reasoning as `readScriptSample` in features/design).
   */
  const readBrainstormContext = useCallback(() => {
    const lines = brainstorm === null ? [] : selectionLines(brainstorm.selection, rowIds, textOf);
    return buildBrainstormContext(rowIds.map(textOf), lines);
  }, [brainstorm, rowIds, textOf]);

  /* ------------------------------ render ------------------------------ */

  if (sheetId === null) {
    return <div className="grid-empty">Loading script…</div>;
  }
  if (colId === undefined) {
    return <div className="grid-empty">This board has no Original Script column.</div>;
  }

  const openThreadInfo =
    openThread !== null ? session.board.getThread(openThread.threadId) : undefined;

  return (
    <div
      className="script-view"
      data-cells-tick={cellsTick}
      // A press anywhere that is not a block, the menu or an open popover
      // dismisses a cross-block highlight — it has no caret of its own to move
      // away from, so nothing else would ever clear it.
      onPointerDown={(event) => {
        const target = event.target;
        if (
          target instanceof Element &&
          target.closest("[data-script-row], .script-popover, .script-sel-menu")
        ) {
          return;
        }
        applyBlockSel(null);
      }}
    >
      <div
        className="script-page"
        style={pageWidth !== undefined ? { maxWidth: pageWidth } : undefined}
        ref={listRef}
        onDragOver={onListDragOver}
        onDrop={onListDrop}
        onDragEnd={() => {
          setDragRowId(null);
          setDropIndex(null);
        }}
      >
        {rowIds.map((rowId, index) => {
          const text = textOf(rowId);
          const rowThreads = threadsByRow.get(rowId) ?? [];
          const heading = shortHeadings.get(rowId);
          return (
            <div key={rowId}>
              {heading !== undefined && <ShortHeading heading={heading} />}
              {dropIndex === index && dragRowId !== null && (
                <div className="script-drop-indicator" />
              )}
              <ScriptBlock
                rowId={rowId}
                text={text}
                marks={marksOf(rowId)}
                types={typeOf(rowId)}
                onSetTextType={
                  frozen
                    ? undefined
                    : (text, at, ty) =>
                        session.setCellTextTypeAt(sheetId, rowId, colId, text, at, ty)
                }
                theme={theme}
                getText={() => textOf(rowId)}
                threads={rowThreads}
                frozen={frozen}
                dragging={dragRowId === rowId}
                registerRef={(el) => {
                  if (el === null) blockRefs.current.delete(rowId);
                  else blockRefs.current.set(rowId, el);
                }}
                onCommit={(value) => {
                  if (value !== text) session.setCellValue(sheetId, rowId, colId, value);
                }}
                onFocusBlock={() =>
                  session.setEditingCell({ sheetId, rowId, colId })
                }
                onBlurBlock={() => session.setEditingCell(undefined)}
                onSplit={splitBlock}
                onMergePrev={index > 0 ? mergeIntoPrevious : undefined}
                onMergeNext={index < rowIds.length - 1 ? mergeNextIn : undefined}
                onPasteLines={pasteLines}
                onArrow={(direction, caretHint) => {
                  const neighbor = rowIds[index + (direction === "up" ? -1 : 1)];
                  if (neighbor !== undefined) {
                    focusBlock(
                      neighbor,
                      direction === "up" ? Number.MAX_SAFE_INTEGER : caretHint,
                    );
                  }
                }}
                onDragStateChange={(active) => {
                  setDragRowId(active ? rowId : null);
                  if (!active) setDropIndex(null);
                }}
                onSelectText={(start, end) =>
                  setTextSel(start === end ? null : { rowId, start, end })
                }
                onSelectionStart={(el) => beginSelectionDrag(rowId, el)}
                onModifierSelect={(mode, el) => selectBlockByClick(rowId, mode, el)}
                onToggleFormat={
                  frozen
                    ? undefined
                    : (kind) =>
                        markSelection((row, start, end) =>
                          session.toggleCellMark(sheetId, row, colId, kind, start, end),
                        )
                }
                // Only a cross-block range paints whole lines. A character
                // range already has the browser's own highlight, and tinting
                // its whole row on top would claim more than is selected.
                selected={selection?.kind === "blocks" && selectedRowSet.has(rowId)}
                asking={asking?.rows.has(rowId) === true}
                menu={
                  menuRow === rowId && selection !== null ? (
                    <SelectionMenu
                      lines={selectedRows.length}
                      // A comment thread anchors to a text range inside ONE
                      // cell (`Anchor.sel`), so there is nothing for a
                      // cross-block highlight to anchor to. Offering a Comment
                      // that quietly attached to only the first line would be
                      // worse than not offering it.
                      onComment={
                        selection.kind === "text"
                          ? () => {
                              const start = Math.min(selection.start, selection.end);
                              const end = Math.max(selection.start, selection.end);
                              setComposer({
                                rowId,
                                sel: { start, end, quote: textOf(rowId).slice(start, end) },
                              });
                              setTextSel(null);
                            }
                          : undefined
                      }
                      onAsk={openBrainstorm}
                      // F-17-marks: formatting acts on a CHARACTER range, so
                      // it is offered for a range inside one block only — a
                      // cross-block highlight has no offsets to mark.
                      format={
                        selection.kind === "text" && !frozen && formatState !== null
                          ? {
                              ...formatState,
                              onToggle: (kind) =>
                                markSelection((row, start, end) =>
                                  session.toggleCellMark(sheetId, row, colId, kind, start, end),
                                ),
                              onColor: (kind, color) =>
                                markSelection((row, start, end) =>
                                  session.setCellMarkColor(
                                    sheetId,
                                    row,
                                    colId,
                                    kind,
                                    start,
                                    end,
                                    color,
                                  ),
                                ),
                            }
                          : undefined
                      }
                      onDelete={
                        selection.kind === "blocks" && !frozen
                          ? () => deleteBlocks(selectedRows)
                          : undefined
                      }
                    />
                  ) : null
                }
                onOpenThread={(threadId) => setOpenThread({ rowId, threadId })}
              />
              {composer !== null && composer.rowId === rowId && (
                <div className="script-popover">
                  <div className="script-popover-quote">“{clip(composer.sel.quote, 80)}”</div>
                  <CommentInput
                    placeholder="Comment on this text…"
                    submitLabel="Comment"
                    autoFocus
                    onSubmit={submitComment}
                    onCancel={() => setComposer(null)}
                  />
                </div>
              )}
              {openThread !== null &&
                openThread.rowId === rowId &&
                openThreadInfo !== undefined && (
                  <div className="script-popover">
                    <ThreadView
                      session={session}
                      thread={openThreadInfo}
                      onClose={() => setOpenThread(null)}
                    />
                  </div>
                )}
              {brainstorm !== null && brainstorm.rowId === rowId && asking !== null && (
                <BrainstormPopover
                  boardId={session.boardId}
                  selection={asking.selection}
                  selectionDropped={asking.dropped}
                  readContext={readBrainstormContext}
                  onClose={() => setBrainstorm(null)}
                />
              )}
            </div>
          );
        })}
        {dropIndex === rowIds.length && dragRowId !== null && (
          <div className="script-drop-indicator" />
        )}
        <button
          type="button"
          className="script-add-block"
          onClick={addBlockAtEnd}
          disabled={frozen}
        >
          ＋ Add a block
        </button>
      </div>
    </div>
  );
}

/* ---------------------------------------------------------------------- */

function ScriptBlock({
  rowId,
  text,
  marks,
  types,
  onSetTextType,
  theme,
  getText,
  threads,
  frozen,
  dragging,
  registerRef,
  onCommit,
  onFocusBlock,
  onBlurBlock,
  onSplit,
  onMergePrev,
  onMergeNext,
  onPasteLines,
  onArrow,
  onDragStateChange,
  onSelectText,
  onSelectionStart,
  onModifierSelect,
  onToggleFormat,
  selected,
  asking,
  menu,
  onOpenThread,
}: {
  rowId: string;
  text: string;
  /** F-17-marks on this block's cell — painted by the mirror layer. */
  marks: CellTextMarks | undefined;
  /** F-17-type: this block's line-kind switches; undefined = all narration. */
  types: readonly TextTypeSwitch[] | undefined;
  /**
   * Switch the line kind at `at` in `text` from the `/` menu — `text` being
   * the draft with the command cut out, which lands in the doc together with
   * the switch. Absent while the board is frozen.
   */
  onSetTextType: ((text: string, at: number, ty: CellTextType | undefined) => void) | undefined;
  /** Theme tokens the highlight's readable-ink derivation computes from. */
  theme: ThemeColors;
  /** CURRENT cell text straight from the doc — see onFocus. */
  getText: () => string;
  threads: readonly CommentThreadInfo[];
  frozen: boolean;
  dragging: boolean;
  registerRef: (el: HTMLTextAreaElement | null) => void;
  onCommit: (value: string) => void;
  onFocusBlock: () => void;
  onBlurBlock: () => void;
  onSplit: (rowId: string, before: string, after: string) => void;
  onMergePrev: ((rowId: string, text: string) => void) | undefined;
  onMergeNext: ((rowId: string, text: string) => void) | undefined;
  onPasteLines: (rowId: string, before: string, lines: string[], after: string) => void;
  onArrow: (direction: "up" | "down", caretHint: number) => void;
  onDragStateChange: (active: boolean) => void;
  onSelectText: (start: number, end: number) => void;
  /** F-17-marks keyboard path (⌘B / ⌘⇧X); absent while the board is frozen. */
  onToggleFormat: ((kind: "bold" | "strike") => void) | undefined;
  /** A press inside this block's text — the start of a possible cross-block drag. */
  onSelectionStart: (el: HTMLTextAreaElement) => void;
  /** Shift+click extends the block range; Cmd/Ctrl+click toggles one block. */
  onModifierSelect: (mode: "extend" | "toggle", el: HTMLTextAreaElement) => void;
  /** In the live highlight (whole-line tint for a cross-block range). */
  selected: boolean;
  /** In the passage an open AI panel is asking about. */
  asking: boolean;
  /** The selection menu, when this is the row it hangs off. */
  menu: ReactNode;
  onOpenThread: (threadId: string) => void;
}) {
  /** Local draft while focused; null = mirror the doc. */
  const [draft, setDraft] = useState<string | null>(null);
  const draftRef = useRef<string | null>(null);
  const commitTimer = useRef<number | null>(null);
  /** True between compositionstart and compositionend (an IME is mid-word). */
  const composing = useRef(false);
  /** When the last composition ended — see isImeEnterEcho. */
  const compositionEndedAt = useRef(Number.NEGATIVE_INFINITY);
  const value = draft ?? text;

  /* ---------------- F-17-type: the `/` menu (ui/text-type.ts) --------- */

  /** Caret offset, or null for "not in the text" (blurred, or Esc-dismissed). */
  const [caret, setCaret] = useState<number | null>(null);
  const [typeMenuActive, setTypeMenuActive] = useState(0);
  const typeTrigger = caret === null ? null : slashQueryAt(value, caret);
  // The menu is DERIVED from the admin-defined kinds (features/text-kinds), so
  // a kind added on /admin is in the next `/` a writer types here.
  const customKinds = useTextKinds();
  const typeMatches =
    typeTrigger === null ? [] : matchTextTypes(typeTrigger.query, textTypeOptions(customKinds));
  const typeMenuOpen = onSetTextType !== undefined && typeMatches.length > 0;
  const typeMenuIndex = Math.min(typeMenuActive, typeMatches.length - 1);

  useEffect(() => {
    setTypeMenuActive(0);
  }, [typeTrigger?.start, typeTrigger?.query]);

  const commit = useCallback(() => {
    if (commitTimer.current !== null) {
      window.clearTimeout(commitTimer.current);
      commitTimer.current = null;
    }
    if (draftRef.current !== null) onCommit(draftRef.current);
  }, [onCommit]);

  // Commit any pending draft when the block unmounts (row deleted elsewhere
  // is fine — setCellValue on a gone row writes an orphan cell, harmless).
  useEffect(
    () => () => {
      if (commitTimer.current !== null) window.clearTimeout(commitTimer.current);
    },
    [],
  );

  const setDraftValue = (next: string) => {
    // The draft is updated even mid-composition: React controls this textarea,
    // so a render that did not know about the composed text would put the old
    // value back and the IME would lose its word.
    draftRef.current = next;
    setDraft(next);
    if (commitTimer.current !== null) window.clearTimeout(commitTimer.current);
    // What the COMMIT must not see, though, is a half-typed word: "ㄋㄧ" is a
    // keystroke buffer, not a value, and committing it would broadcast it to
    // everyone on the board and leave it in the undo history. compositionend
    // schedules the real commit, with the character the writer chose.
    if (composing.current) return;
    commitTimer.current = window.setTimeout(() => {
      commitTimer.current = null;
      if (draftRef.current !== null) onCommit(draftRef.current);
    }, 400);
  };

  /**
   * Take an option from the `/` menu.
   *
   * A KIND cuts the `/query` out of the draft and puts a switch point where
   * the `/` stood. The cut draft and the switch land in the doc TOGETHER,
   * now — a switch is an offset into the cell's text, so the text has to be
   * there first (the block's half of `flushBlock`). The debounced commit that
   * was pending held exactly this draft plus the command; it is superseded.
   *
   * A SNIPPET is just text: it goes through `setDraftValue` and rides the
   * block's ordinary 400 ms commit debounce rather than jumping the queue —
   * a pick is an edit like any other keystroke.
   */
  const pickTextType = (option: TextTypeOption, el: HTMLTextAreaElement) => {
    if (typeTrigger === null || caret === null || onSetTextType === undefined) return;
    const next = applyTextTypePick(value, caret, typeTrigger.start, option);
    if (option.kind === "type") {
      if (commitTimer.current !== null) {
        window.clearTimeout(commitTimer.current);
        commitTimer.current = null;
      }
      draftRef.current = next.text;
      setDraft(next.text);
      onSetTextType(next.text, next.caret, option.type ?? undefined);
    } else {
      setDraftValue(next.text);
    }
    setCaret(next.caret);
    // After React has written the new draft.
    requestAnimationFrame(() => {
      el.focus();
      el.setSelectionRange(next.caret, next.caret);
    });
  };

  /**
   * The mirror's classes, and whether it is showing the finished form. `draft`
   * is non-null exactly while this block is focused (set on focus, cleared on
   * blur), which makes it the "is being edited" test.
   */
  const typeShown = types !== undefined && draft === null;
  const renderClass = typeShown ? "script-block-render tt-shown" : "script-block-render";

  const onKeyDown = (event: ReactKeyboardEvent<HTMLTextAreaElement>) => {
    const el = event.currentTarget;
    // An IME candidate popup is open: this Enter picks the character, this
    // Backspace deletes a keystroke, these arrows walk the candidate list.
    // None of them are the block's — reading them as such is what split the
    // block under a writer choosing a Chinese character (core/keyboard.ts).
    if (isImeComposing(event.nativeEvent)) return;
    if (isImeEnterEcho(event.key, performance.now() - compositionEndedAt.current)) {
      // Swallowed rather than passed on: the echo would otherwise land in the
      // block as a newline, which is not what picking a candidate means either.
      event.preventDefault();
      return;
    }
    // F-17-type: while the `/` menu is open it OWNS these keys. Enter must
    // take the armed kind rather than SPLIT the block, which is the one that
    // would really hurt — a split is a structural edit, not a typo.
    if (typeMenuOpen) {
      if (event.key === "ArrowDown") {
        event.preventDefault();
        setTypeMenuActive((i) => (i + 1) % typeMatches.length);
        return;
      }
      if (event.key === "ArrowUp") {
        event.preventDefault();
        setTypeMenuActive((i) => (i - 1 + typeMatches.length) % typeMatches.length);
        return;
      }
      if (event.key === "Enter" || event.key === "Tab") {
        event.preventDefault();
        const option = typeMatches[typeMenuIndex];
        if (option !== undefined) pickTextType(option, el);
        return;
      }
      if (event.key === "Escape") {
        event.preventDefault();
        // Closes the MENU only — the writer is dismissing a command they did
        // not mean to start, and the line they are typing is untouched.
        setCaret(null);
        return;
      }
    }
    // F-17-marks shortcuts, the two everybody already has in their fingers.
    // They act on the SELECTION, so with a bare caret they fall through to the
    // browser (⌘B does nothing in a textarea anyway) rather than swallowing a
    // key the writer may have meant for something else.
    if (el.selectionStart !== el.selectionEnd) {
      const kind = formatShortcut(event);
      if (kind !== null && onToggleFormat !== undefined) {
        event.preventDefault();
        onToggleFormat(kind);
        return;
      }
    }
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      if (frozen) return;
      commit();
      draftRef.current = null;
      setDraft(null);
      onSplit(rowId, value.slice(0, el.selectionStart), value.slice(el.selectionEnd));
      return;
    }
    if (
      event.key === "Backspace" &&
      el.selectionStart === 0 &&
      el.selectionEnd === 0 &&
      onMergePrev !== undefined
    ) {
      event.preventDefault();
      if (frozen) return;
      if (commitTimer.current !== null) window.clearTimeout(commitTimer.current);
      draftRef.current = null;
      setDraft(null);
      onMergePrev(rowId, value);
      return;
    }
    if (
      event.key === "Delete" &&
      el.selectionStart === value.length &&
      el.selectionEnd === value.length &&
      onMergeNext !== undefined
    ) {
      event.preventDefault();
      if (frozen) return;
      if (commitTimer.current !== null) window.clearTimeout(commitTimer.current);
      draftRef.current = null;
      setDraft(null);
      onMergeNext(rowId, value);
      return;
    }
    if (event.key === "ArrowUp" && el.selectionStart === 0 && el.selectionEnd === 0) {
      event.preventDefault();
      onArrow("up", 0);
      return;
    }
    if (
      event.key === "ArrowDown" &&
      el.selectionStart === value.length &&
      el.selectionEnd === value.length
    ) {
      event.preventDefault();
      onArrow("down", 0);
      return;
    }
  };

  const onPaste = (event: ReactClipboardEvent<HTMLTextAreaElement>) => {
    // Blocks copied from the script carry their own boundaries; anything else
    // is plain text, one block per line (./clipboard.ts).
    const copied = decodeBlocks(event.clipboardData.getData(SCRIPT_BLOCKS_MIME));
    const plain = event.clipboardData.getData("text/plain");
    const lines = copied ?? textToBlocks(plain);
    // One line that is exactly what was on the clipboard = a plain inline
    // paste, which the browser does better than we can (caret, undo, scroll).
    if (lines.length === 1 && copied === null && lines[0] === plain) return;
    event.preventDefault();
    if (frozen) return;
    const el = event.currentTarget;
    commit();
    draftRef.current = null;
    setDraft(null);
    onPasteLines(rowId, value.slice(0, el.selectionStart), lines, value.slice(el.selectionEnd));
  };

  const highlights = useMemo(() => highlightsFor(value, threads), [value, threads]);

  const segments = useMemo(
    () => blockSegments(value, marks, types, highlights),
    [value, marks, types, highlights],
  );

  const openCount = threads.filter((t) => !t.resolved).length;

  return (
    <div
      className={`script-block${dragging ? " dragging" : ""}${selected ? " selected" : ""}${
        asking ? " asking" : ""
      }`}
      data-script-row={rowId}
    >
      <div className="script-gutter">
        <span
          className="script-drag-handle"
          data-tip="Drag to reorder"
          draggable={!frozen}
          onDragStart={(event) => {
            event.dataTransfer.setData(ROW_DRAG_MIME, rowId);
            event.dataTransfer.effectAllowed = "move";
            onDragStateChange(true);
          }}
          onDragEnd={() => onDragStateChange(false)}
        >
          ⠿
        </span>
        {openCount > 0 && (
          <button
            type="button"
            className="script-thread-badge"
            data-tip={`${openCount} comment${openCount > 1 ? "s" : ""}`}
            onClick={() => {
              const first = threads.find((t) => !t.resolved) ?? threads[0];
              if (first !== undefined) onOpenThread(first.id);
            }}
          >
            💬{openCount}
          </button>
        )}
      </div>
      <div className="script-block-body">
        {/* Mirror layer: identical text metrics, transparent glyphs — it only
            paints the highlight marks (and defines the block's height). */}
        {/* F-17-type: the line kinds, in two halves. The metric-neutral one
            (each run's ink and painted weight, via its segment class) is
            always on. The width-changing one (`tt-shown` — the italic, and
            the brackets around each sound/visual run) is worn only while the
            block is NOT being edited: it would otherwise slide every glyph
            out from under the caret of the transparent textarea above it
            (ui/MarkedText.tsx). Unfocused there is no caret to keep aligned,
            and the mirror is what defines the block's height anyway, so the
            wider text simply gets the room it needs. */}
        <div className={renderClass} aria-hidden>
          {segments.map((segment, i) => {
            const format = markClassName(segment);
            const style = markStyle(segment, theme.surface, theme.text);
            const node =
              segment.threadId !== undefined ? (
                <mark
                  className={format === undefined ? "script-mark" : `script-mark ${format}`}
                  style={style}
                >
                  {segment.text}
                </mark>
              ) : (
                <span className={format} style={style}>
                  {segment.text}
                </span>
              );
            if (!typeShown) return <Fragment key={i}>{node}</Fragment>;
            const { opens, closes } = typeRunEdges(segments, i);
            const [open, close] = textTypeBrackets(segment.type);
            return (
              <Fragment key={i}>
                {opens && <span className="tt-bracket">{open}</span>}
                {node}
                {closes && <span className="tt-bracket">{close}</span>}
              </Fragment>
            );
          })}
          {"​"}
        </div>
        <textarea
          ref={registerRef}
          className="script-block-input"
          value={value}
          readOnly={frozen}
          rows={1}
          onFocus={() => {
            // Read the doc, NOT the rendered `value`: a structural edit
            // (merge/split) focuses this block synchronously, before React
            // has re-rendered the new text — capturing the stale render value
            // as the draft would clobber the doc on the next commit.
            const current = getText();
            draftRef.current = current;
            setDraft(current);
            setCaret(null);
            onFocusBlock();
          }}
          onBlur={() => {
            commit();
            draftRef.current = null;
            setDraft(null);
            setCaret(null);
            onBlurBlock();
            // Late clear so a click on the Comment button (pointerdown
            // preventDefault keeps focus) is not raced out by blur.
            window.setTimeout(() => onSelectText(0, 0), 0);
          }}
          onChange={(event) => {
            setDraftValue(event.target.value);
            setCaret(event.target.selectionStart);
          }}
          onCompositionStart={() => {
            composing.current = true;
          }}
          onCompositionEnd={(event) => {
            composing.current = false;
            compositionEndedAt.current = performance.now();
            // The chosen character is in the field now — this is the value the
            // held-back commit was waiting for.
            setDraftValue(event.currentTarget.value);
          }}
          onKeyDown={onKeyDown}
          onPaste={onPaste}
          onPointerDown={(event) => {
            // Primary button only: a right-click opens the context menu and a
            // middle-click pastes on Linux — neither begins a highlight.
            if (event.button !== 0) return;
            // A modified click selects WHOLE BLOCKS, so it must not also drop a
            // caret or begin a character drag: preventDefault stops the
            // textarea taking focus in the first place.
            if (event.shiftKey || event.metaKey || event.ctrlKey) {
              event.preventDefault();
              onModifierSelect(event.shiftKey ? "extend" : "toggle", event.currentTarget);
              return;
            }
            onSelectionStart(event.currentTarget);
          }}
          onSelect={(event) => {
            // An IME highlighting the clause it is converting is not a text
            // selection the writer made — floating the Comment/Ask AI menu over
            // a half-typed word would be the same mistake as splitting on its
            // Enter.
            if (composing.current) return;
            const el = event.currentTarget;
            onSelectText(el.selectionStart, el.selectionEnd);
            // Caret moves without edits open and close the `/` menu too.
            setCaret(el.selectionStart);
          }}
          onClick={(event) => {
            const el = event.currentTarget;
            if (el.selectionStart !== el.selectionEnd) return;
            const offset = el.selectionStart;
            const hit = highlights.find((h) => offset >= h.start && offset < h.end);
            if (hit !== undefined) onOpenThread(hit.threadId);
          }}
        />
        {typeMenuOpen && (
          <ul className="gt-type-menu script-type-menu" role="listbox" aria-label="Line type">
            {typeMatches.map((option, i) => (
              <li key={option.label} role="presentation">
                <button
                  type="button"
                  role="option"
                  aria-selected={i === typeMenuIndex}
                  className={`gt-type-option${i === typeMenuIndex ? " active" : ""}`}
                  // Pointer-DOWN: a click blurs the textarea first, and blur
                  // clears the draft this pick is about to rewrite.
                  onMouseDown={(event) => {
                    event.preventDefault();
                    const el = event.currentTarget
                      .closest(".script-block-body")
                      ?.querySelector("textarea");
                    if (el instanceof HTMLTextAreaElement) pickTextType(option, el);
                  }}
                  onMouseEnter={() => setTypeMenuActive(i)}
                >
                  <span
                    className={`gt-type-swatch ${textTypeSwatch(option).className}`}
                  >
                    {textTypeSwatch(option).glyph}
                  </span>
                  <span className="gt-type-label">{option.label}</span>
                  <span className="gt-type-hint">{option.hint}</span>
                </button>
              </li>
            ))}
          </ul>
        )}
        {menu}
      </div>
    </div>
  );
}

/**
 * The floating menu over a highlight. Every item uses pointerdown +
 * preventDefault rather than click: a click would first move focus out of the
 * textarea, and the browser drops the text selection on the way — taking the
 * range the item was about to act on with it.
 */
function SelectionMenu({
  lines,
  onComment,
  onAsk,
  onDelete,
  format,
}: {
  lines: number;
  /** Absent for a cross-block highlight — a thread has no range to anchor to. */
  onComment: (() => void) | undefined;
  onAsk: () => void;
  /**
   * Absent for a character range (deleting "these three words" is what typing
   * over them already does) and while the board is frozen.
   */
  onDelete: (() => void) | undefined;
  /**
   * F-17-marks over the highlighted characters. Absent for a cross-block
   * highlight (no offsets to mark) and while the board is frozen.
   */
  format: TextFormatActions | undefined;
}) {
  return (
    <div className="script-sel-menu" role="menu">
      {format !== undefined && (
        <>
          <TextFormatControls format={format} />
          <span className="script-sel-divider" />
        </>
      )}
      {onComment !== undefined && (
        <button
          type="button"
          className="script-sel-item"
          role="menuitem"
          onPointerDown={(event) => {
            event.preventDefault();
            onComment();
          }}
        >
          💬 Comment
        </button>
      )}
      <button
        type="button"
        className="script-sel-item ai"
        role="menuitem"
        title="Ask AI about the highlighted script"
        onPointerDown={(event) => {
          event.preventDefault();
          onAsk();
        }}
      >
        <SparkleIcon /> Ask AI
      </button>
      {onDelete !== undefined && (
        <button
          type="button"
          className="script-sel-item danger"
          role="menuitem"
          title={`Delete ${lines === 1 ? "this block" : `these ${lines} blocks`} (Delete)`}
          onPointerDown={(event) => {
            event.preventDefault();
            onDelete();
          }}
        >
          🗑 Delete
        </button>
      )}
      {lines > 1 && <span className="script-sel-count">{lines} lines</span>}
    </div>
  );
}

function clip(text: string, max: number): string {
  return text.length > max ? `${text.slice(0, max - 1)}…` : text;
}

/** F-142: what the heading over a short's first row shows. */
interface ShortHeadingInfo {
  slot: number;
  /** "" until someone writes one. */
  title: string;
  /** `YYYY-MM-DD`; undefined when the slot is missing from the summary. */
  releaseDate: string | undefined;
  state: ShortState;
}

function ShortHeading({ heading }: { heading: ShortHeadingInfo }) {
  return (
    <div className="script-short-heading" role="heading" aria-level={2}>
      <span className={`shorts-dot shorts-dot-${heading.state}`} aria-hidden="true" />
      <span className="script-short-heading-slot">Short {heading.slot}</span>
      {heading.releaseDate !== undefined && (
        <>
          <span className="script-short-heading-sep" aria-hidden="true">
            ·
          </span>
          <span className="script-short-heading-date">
            {formatDateKeyWithWeekday(heading.releaseDate)}
          </span>
        </>
      )}
      {heading.title !== "" && (
        <>
          <span className="script-short-heading-sep" aria-hidden="true">
            ·
          </span>
          <span className="script-short-heading-title">{heading.title}</span>
        </>
      )}
    </div>
  );
}
